
import React from 'react';
import { FormField, FormItem, FormLabel, FormDescription, FormMessage } from '@/components/ui/form';
import { UseFormReturn } from 'react-hook-form';
import EnhancedBookSelectionCard, { EnhancedBookData } from './EnhancedBookSelectionCard';
import { BookFormat } from '@/types';

interface BookSelectionProps {
  form: UseFormReturn<any>;
  authorBooks: any[];
  bookFormatGroups: Record<string, EnhancedBookData[]>;
  onToggleBook: (bookId: string, checked: boolean) => void;
  onUpdateBookData: (formatId: string, field: keyof Omit<EnhancedBookData, 'bookId' | 'formatId'>, value: number | BookFormat | string) => void;
  onAddFormat: (bookId: string) => void;
  onRemoveFormat: (formatId: string) => void;
}

export default function BookSelection({
  form,
  authorBooks,
  bookFormatGroups,
  onToggleBook,
  onUpdateBookData,
  onAddFormat,
  onRemoveFormat
}: BookSelectionProps) {
  // Group books by title
  const booksByTitle = React.useMemo(() => {
    const grouped: Record<string, any[]> = {};
    authorBooks.forEach((book) => {
      if (!grouped[book.title]) {
        grouped[book.title] = [];
      }
      grouped[book.title].push(book);
    });
    return grouped;
  }, [authorBooks]);

  return (
    <FormField
      control={form.control}
      name="books"
      render={() => (
        <FormItem>
          <FormLabel>Books & Sales Data</FormLabel>
          <FormDescription>
            Select books by title and enter detailed sales data for each book format. Each format can have a different country and ISBN.
          </FormDescription>
          <div className="max-h-96 overflow-y-auto space-y-3 border rounded-md p-3">
            {Object.entries(booksByTitle).map(([title, books]) => {
              // Check if any book in this title group has selected formats
              const hasSelectedFormats = books.some(book => bookFormatGroups[book.id]?.length > 0);
              
              return (
                <EnhancedBookSelectionCard
                  key={title}
                  title={title}
                  books={books}
                  hasSelectedFormats={hasSelectedFormats}
                  bookFormatGroups={bookFormatGroups}
                  onUpdateBookData={onUpdateBookData}
                  onAddFormat={onAddFormat}
                  onRemoveFormat={onRemoveFormat}
                  form={form}
                />
              );
            })}
          </div>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
